import { Reservation, Seat, SeatType } from "@/graphql/types"
import { useCallback, useMemo, useState } from "react"
import styled from "styled-components"
import SeatDisplay from "./seatDisplay"

type Props = {
  seats: Seat[]
  onSeatClicked: (seat: Seat | undefined) => boolean
  size: "small" | "large"
  reservations?: Reservation[]
  seatTypes: SeatType[]
  colorRule: (seat: Seat) => string | undefined
  lockRule: (seat: Seat) => boolean
}

const SeatsDisplay = (props: Props) => {
  const [clickedSeatIndex, setClickedSeatIndex] = useState<number>(-1)

  const onSeatClicked = useCallback((seat: Seat) => {
    const index = props.seats.findIndex((primeSeat) => {
      return seat.column === primeSeat.column && seat.row === primeSeat.row
    })
    if(clickedSeatIndex === index) {
      setClickedSeatIndex(-1)
      props.onSeatClicked(undefined)
    } else {
      const result = props.onSeatClicked(seat)
      if(result) {
        setClickedSeatIndex(index)
      }
    }
  }, [clickedSeatIndex, props])

  const colorRule = useCallback((seat: Seat) => {
    if(props.colorRule(seat)) {
      return props.colorRule(seat)
    }
    if(seat.type === -1) {
      return "transparent"
    }
    const index = props.seats.findIndex((primeSeat) => {
      return seat.column === primeSeat.column && seat.row === primeSeat.row
    })
    if(index === clickedSeatIndex) {
      return "red"
    }
    if(seat.locked) {
      return "gray"
    }
    return undefined
  }, [clickedSeatIndex, props])

  const { row, column } = useMemo(() => {
    if(!props.seats.length) {
      return {
        row: 0,
        column: 0,
      }
    }
    const tree: { [row: string]: { [column: string]: number } } = {}
    props.seats.forEach((seat, index) => {
      if(!tree[seat.row]) {
        tree[seat.row] = {}
      }
      tree[seat.row][seat.column] = index
    })
    const row = Object.keys(tree).length
    const column = Object.keys(tree[1])?.length ?? 0

    return {
      row,
      column,
    }
  }, [props])

  const rowStyleObj = useMemo(() => {
    if(props.size === "small") {
      return {
        width: `${column * 50}px`,
      }
    } else {
      return {
        width: `${column * 80}px`,
      }
    }
  }, [column, props.size])

  const seatsArea = useMemo(() => {
    console.log(props.seats.length)
    const seatsObj = []
    for(let i = 1; i <= row; i++) {
      const rowArray = []
      const rowSeats = props.seats.filter((seat) => seat.row === i)
      for(let j = 1; j <= column; j++) {
        const hitSeat = rowSeats.find((seat) => seat.column === j)
        if(hitSeat) {
          rowArray.push((
            <SeatDisplay 
              seat={hitSeat}
              reservations={props.reservations ?? []}
              seatTypes={props.seatTypes}
              size={props.size}
              colorRule={colorRule}
              onClick={onSeatClicked}
              key={`seat-${i}-${j}`}
              lockRule={props.lockRule}
            />
          ))
        }
      }
      const rowObj = (
        <div className={`row ${props.size}`} style={rowStyleObj}>
          {rowArray}
        </div>
      )
      seatsObj.push(rowObj)
    }
    return (
      <div className={`seats ${props.size}`}>
        {seatsObj}
      </div>
    )
  }, [colorRule, column, onSeatClicked, props.lockRule, props.reservations, props.seatTypes, props.seats, props.size, row, rowStyleObj])

  const seatStyleObj = useMemo(() => {
    if(props.size === "small") {
      return {
        height: `${row * 50}px`,
        width: `${column * 50}px`,
        margin: "auto",
      }
    } else {
      return {
        height: `${row * 80}px`,
        width: `${column * 80}px`,
        margin: "auto",
      }
    }
  }, [column, props.size, row])


  const stageStyleObj = useMemo(() => {
    if(props.size === "small") {
      return {
        width: `${column * 50}px`,
      }
    } else {
      return {
        width: `${column * 80}px`,
      }
    }
  }, [column, props.size])

  return (
    <SeatsDisplayStyle>
      <div className="stage-area" style={stageStyleObj}>
        <p>舞台側</p>
      </div>
      <div style={seatStyleObj}>
        { seatsArea }
      </div>
    </SeatsDisplayStyle>
  )
}

const SeatsDisplayStyle = styled.div`
  .stage-area {
    width: 100%;
    height: 80px;
    background-color: black;
    margin: 0 auto 8px auto;
    color: white;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    font-weight: 700;
  }
  .seats {
    width: 100%;
    display: flex;
    flex-direction: column;
    &.small {
      gap: 5px;
    }
    &.large {
      gap: 10px;
    }
    .row {
      display: flex;
      justify-content: center;
      &.small {
        gap: 5px;
      }
      &.large {
        gap: 10px;
      }
    }
  }
`

export default SeatsDisplay